import { useState, useEffect, useCallback } from 'react';
import type {
  SessionId,
  SessionState,
  Participant,
  VoteValue,
} from '../types/index';
import { getFacilitatorToken, saveFacilitatorToken } from '../utils/localStorage';

export interface UseSessionReturn {
  sessionId: SessionId | null;
  state: SessionState;
  participants: Participant[];
  story: string | undefined;
  myVote: VoteValue | null;
  isFacilitator: boolean;
  expiresAt: number | null;
  voteStartTime: number | null;
  setSessionId: (id: SessionId | null) => void;
  setState: (state: SessionState) => void;
  setParticipants: (participants: Participant[]) => void;
  setStory: (story: string | undefined) => void;
  setMyVote: (vote: VoteValue | null) => void;
  setIsFacilitator: (isFacilitator: boolean) => void;
  setExpiresAt: (expiresAt: number | null) => void;
  setVoteStartTime: (voteStartTime: number | null) => void;
  setFacilitatorToken: (sessionId: string, token: string) => void;
}

export function useSession(): UseSessionReturn {
  const [sessionId, setSessionId] = useState<SessionId | null>(null);
  const [state, setState] = useState<SessionState>('collecting');
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [story, setStory] = useState<string | undefined>(undefined);
  const [myVote, setMyVote] = useState<VoteValue | null>(null);
  const [isFacilitator, setIsFacilitator] = useState(false);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [voteStartTime, setVoteStartTime] = useState<number | null>(null);

  const setFacilitatorToken = useCallback((sessionId: string, token: string) => {
    saveFacilitatorToken(sessionId, token);
    setIsFacilitator(true);
  }, []);

  // Check if user is facilitator when sessionId changes
  useEffect(() => {
    if (sessionId) {
      const token = getFacilitatorToken(sessionId);
      setIsFacilitator(!!token);
    } else {
      setIsFacilitator(false);
    }
  }, [sessionId]);

  return {
    sessionId,
    state,
    participants,
    story,
    myVote,
    isFacilitator,
    expiresAt,
    voteStartTime,
    setSessionId,
    setState,
    setParticipants,
    setStory,
    setMyVote,
    setIsFacilitator,
    setExpiresAt,
    setVoteStartTime,
    setFacilitatorToken,
  };
}

